import React from 'react';
import { motion } from 'framer-motion';
import { Building2Icon, Globe2Icon, MapPinIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
type Company = {
  name: string;
  logo?: string;
};
const COMPANIES: Company[] = [{
  name: 'Global pharma company'
}, {
  name: 'LEO Pharma',
  logo: 'https://cdn.magicpatterns.com/uploads/qe6bAu8fK165LTd9pa9Tmm/Screenshot_2026-08-20_at_21.22.35.png'
}, {
  name: '3M and Solventum',
  logo: 'https://cdn.magicpatterns.com/uploads/8a94AChcQKnfAzckma8QZD/Screenshot_2026-08-27_at_12.40.13.png'
}, {
  name: 'AbbVie',
  logo: 'https://cdn.magicpatterns.com/uploads/pKJuSjA5sXAAftL8qfcZvo/Screenshot_2026-08-27_at_12.39.43.png'
}, {
  name: 'Healthcare company',
  logo: 'https://cdn.magicpatterns.com/uploads/aQbK55PqMKmQ6yF9dTcsFH/Screenshot_2026-08-20_at_21.22.20.png'
}];
const REGIONS = ['Europe', 'Gulf', 'Africa', 'Asia', 'South America'] as const;
const MARKETS = ['Finland', 'Denmark', 'Germany', 'Poland', 'France', 'Netherlands', 'United Kingdom', 'Spain', 'Italy', 'Gulf', 'Morocco', 'Ivory Coast', 'Nigeria', 'South Africa', 'Kenya', 'Singapore', 'China', 'Peru', 'Brazil', 'Ireland', 'Sweden', 'Romania', 'Hungary'] as const;
export function SlideOperatingScale({}: SlideProps) {
  return <InteriorSlide surface data-id="element-436" data-mp="pjsus99tbucr">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="mb-[clamp(1.5rem,2.4vw,2.5rem)] flex-shrink-0" data-id="element-437" data-mp="1tf4ooasg5kt">
        <p className="mb-3 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-438" data-mp="aumoy2ggz5vw">
          Global delivery experience
        </p>
        <h2 className="max-w-6xl text-[clamp(2rem,3.8vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-439" data-mp="6uw2gaxz7bx6">
          Less than a year.{' '}
          <span className="text-[var(--brand)]" data-id="element-440" data-mp="1i758dtxzwrtx">Already at scale.</span>
        </h2>
        <p className="mt-4 text-[clamp(1.1rem,1.9vw,1.85rem)] font-bold leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-441" data-mp="evpsoavd95f6">
          New technology. Decades of know-how.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-442" data-mp="ow9xfni3gs25">
        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.18,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl border border-black/5 bg-white p-[clamp(1.25rem,2vw,2rem)] shadow-sm lg:col-span-4" data-id="element-443" data-mp="swts8kf73jl4">
          <div className="flex items-start justify-between gap-4" data-id="element-444" data-mp="55en6jw1ao66">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-445" data-mp="9xxzzemwdf9x">
              <Building2Icon className="h-5 w-5" aria-hidden="true" data-id="element-446" data-mp="39kebvu9sy0p2" />
            </div>
            <span className="text-[clamp(2.5rem,4vw,4rem)] font-extrabold leading-none text-[var(--brand)]" data-id="element-447" data-mp="3dlpxzptgw3si">
              5
            </span>
          </div>
          <h3 className="mt-4 text-xl font-bold text-[var(--fg)]" data-id="element-448" data-mp="3145my94tl0wb">
            Companies with managed campaigns
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[var(--muted)]" data-id="element-449" data-mp="2x1sirp5q1b1n">
            Selected organizations supported through continued campaign delivery.
          </p>
          <div className="mt-5 grid flex-1 grid-cols-2 gap-3" data-id="element-450" data-mp="2n4e3vtzg4ubg">
            {COMPANIES.map((company, index) => <figure key={company.name} className={`flex min-h-[4rem] items-center justify-center overflow-hidden rounded-2xl border border-[var(--brand)]/10 bg-white px-4 py-2 ${index === COMPANIES.length - 1 ? 'col-span-2' : ''}`} data-id="element-451" data-mp="2ido02l6o4ziv" data-mpi={index}>
                {company.logo ? <img src={company.logo} alt={`${company.name} logo`} className="h-full max-h-14 w-full object-contain" data-id="element-452" data-mp="29ekf1bnxya1q" /> : <figcaption className="text-center text-[0.68rem] font-bold uppercase leading-tight tracking-[0.1em] text-[var(--muted)]" data-id="element-453" data-mp="1zejuxni8zpwh">
                  {company.name}
                </figcaption>}
              </figure>)}
          </div>
        </motion.article>

        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.55
      }} className="relative flex min-h-0 flex-col justify-between overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.4rem,2.2vw,2.25rem)] text-white shadow-lg lg:col-span-3" data-id="element-454" data-mp="3pr9k3zxwtohx">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full border-[28px] border-white/10" data-id="element-455" data-mp="3kthu5bgtscgd" />
          <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15" data-id="element-456" data-mp="36ui5i3tbbxk7">
            <div className="h-6 w-6" aria-hidden="true" data-id="element-457" data-mp="3btbizw8lnokd" />
          </div>
          <div className="relative" data-id="element-458" data-mp="151k43jlgzwnn">
            <p className="text-[clamp(4.5rem,8vw,7.5rem)] font-extrabold leading-none tracking-[-0.06em]" data-id="element-459" data-mp="1a03r7nbsvehl">
              22
            </p>
            <h3 className="mt-3 text-[clamp(1.25rem,2vw,1.75rem)] font-bold" data-id="element-460" data-mp="2uj7juonn2854">
              Brands campaigned
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-white/75" data-id="element-461" data-mp="2pkwcckktg9q2">
              Across different portfolios, lifecycle stages, and HCP needs.
            </p>
            <p className="mt-5 border-t border-white/20 pt-4 text-xs font-semibold uppercase tracking-[0.14em] text-white/65" data-id="element-462" data-mp="2zgz9td4q3k6o">
              Continued delivery experience
            </p>
          </div>
        </motion.article>

        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.42,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl border border-[var(--brand)]/10 bg-white p-[clamp(1.25rem,2vw,2rem)] shadow-sm lg:col-span-5" data-id="element-463" data-mp="3891938322ufd">
          <div className="flex items-start justify-between gap-4" data-id="element-464" data-mp="1ntplvy8t6vn3">
            <div className="flex items-center gap-3" data-id="element-465" data-mp="1nvk4v70isdoj">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-466" data-mp="2dimw84cq05q3">
                <Globe2Icon className="h-5 w-5" aria-hidden="true" data-id="element-467" data-mp="2hjyoyiqw572i" />
              </span>
              <div data-id="element-468" data-mp="2mhrtl7cjpt0o">
                <p className="text-[clamp(2rem,3.2vw,3rem)] font-extrabold leading-none text-[var(--fg)]" data-id="element-469" data-mp="2mh89pl3c4uk0">
                  5
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-470" data-mp="2wh8yhmjz42vl">
                  Regions covered
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-471" data-mp="359awzfahf938">
              <p className="text-[clamp(2rem,3.2vw,3rem)] font-extrabold leading-none text-[var(--brand)]" data-id="element-472" data-mp="35acarrivu9on">
                23
              </p>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-473" data-mp="3f776k2nrtvz8">
                Localizations
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2" data-id="element-474" data-mp="3k47defsg32re">
            {REGIONS.map(region => <span key={region} className="rounded-full border border-[var(--brand)]/15 bg-[var(--brand)]/5 px-3 py-1.5 text-xs font-bold text-[var(--brand)]" data-id="element-475" data-mp="18kj9g5pqvf0h">
                {region}
              </span>)}
          </div>

          <div className="mt-5 flex min-h-0 flex-1 flex-col rounded-2xl bg-[var(--surface)] p-4" data-id="element-476" data-mp="ymln5nrg3sai">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[var(--fg)]" data-id="element-477" data-mp="to1znix7e8g1">
              <MapPinIcon className="h-4 w-4 text-[var(--brand)]" aria-hidden="true" data-id="element-478" data-mp="1rbiqmkqzzkj7" />
              Market footprint
            </div>
            <ul className="grid flex-1 grid-cols-3 gap-x-4 gap-y-1.5" data-id="element-479" data-mp="1hj4o3ps3i9vz">
              {MARKETS.map(market => <li key={market} className="flex items-center gap-1.5 text-[0.68rem] font-medium leading-tight text-[var(--muted)]" data-id="element-480" data-mp="1ckti5q4x8jj1">
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand)]" data-id="element-481" data-mp="2a0pgqsu4fef1" />
                  {market}
                </li>)}
            </ul>
          </div>
        </motion.article>
      </div>
    </InteriorSlide>;
}